import React, { FC, JSX } from 'react';
import { useSelector } from 'react-redux';
import { Route, Routes } from 'react-router-dom';

import { getRoute } from 'src/routes';
import { MobileResolutionPage, Maintenance503Page, NotFound404Page, ServerError500Page } from 'src/shared/components';
import { FeaturesEnums } from 'src/shared/enums';
import { ComponentWithFeatures, ComponentWithSecureRoute } from 'src/shared/hocs';
import { useMobile } from 'src/shared/hooks';
import { RootState } from 'src/store/types';
import { hasCriticalError } from 'src/store/user/user.selectors';

import {
  AccrualSummaryPage,
  CalendarPage,
  HomePage,
  LoginPage,
  SettingsPage,
  MessageCenterPage,
  TimesheetsPage,
} from './pages';

export const MainRoutes: FC = () => {
  const { isInMaintenance } = useSelector((state: RootState) => state.user);

  if (isInMaintenance) {
    return (
      <Routes>
        <Route path="*" element={<Maintenance503Page />} />
      </Routes>
    );
  }

  return (
    <Routes>
      <Route path={getRoute('login')} element={<LoginPage />} />
      <Route
        path="*"
        element={
          <ComponentWithSecureRoute>
            <ProtectedRoutes />
          </ComponentWithSecureRoute>
        }
      />
    </Routes>
  );
};

const ProtectedRoutes = (): JSX.Element => {
  const hasCriticalError500 = useSelector(hasCriticalError);
  const { isMobile } = useMobile();

  if (hasCriticalError500) {
    return (
      <Routes>
        <Route path="*" element={<ServerError500Page />} />
      </Routes>
    );
  }

  if (isMobile) {
    return (
      <Routes>
        <Route path="*" element={<MobileResolutionPage />} />
      </Routes>
    );
  }

  return (
    <Routes>
      <Route path={getRoute('root')} element={<HomePage />} />
      <Route
        path={getRoute('calendar')}
        element={
          <ComponentWithFeatures features={FeaturesEnums.EmployeeCalendar} redirectTo={getRoute('root')}>
            <CalendarPage />
          </ComponentWithFeatures>
        }
      />
      <Route
        path={getRoute('messageCenter')}
        element={
          <ComponentWithFeatures features={FeaturesEnums.Notifications} redirectTo={getRoute('root')}>
            <MessageCenterPage />
          </ComponentWithFeatures>
        }
      />
      <Route
        path={getRoute('balances')}
        element={
          <ComponentWithFeatures
            strict
            features={[FeaturesEnums.AccrualSummary, FeaturesEnums.AccrualsFunction]}
            redirectTo={getRoute('root')}
          >
            <AccrualSummaryPage />
          </ComponentWithFeatures>
        }
      />
      <Route
        path={getRoute('timesheets')}
        element={
          <ComponentWithFeatures features={FeaturesEnums.Timesheet} redirectTo={getRoute('root')}>
            <TimesheetsPage />
          </ComponentWithFeatures>
        }
      />
      <Route path={getRoute('settings')} element={<SettingsPage />} />
      <Route path="*" element={<NotFound404Page />} />
    </Routes>
  );
};
